import { useState } from 'react';
import { useT } from '@dia/i18n';
import { useSyncStatus } from '@dia/db';
import { LangToggle, Screen, StatusPill, SyncBadge } from '@dia/ui';
import { useAuth } from '../auth';
import { sync } from '../supabase';
import { BedIcon, BoatIcon, BubblesIcon, CloudSunIcon, HouseIcon, PalmIcon, SunIcon } from './parts/shellIcons';
import { delay, MotionStyles, useIntro } from './parts/motion';
import { PhotoPanel } from './parts/PhotoPanel';
import { Tile } from './parts/Tile';
import { useAccess } from './parts/useAccess';
import { useRoomsAtAGlance, useSavedWeather, useStayRow, useSunshine } from './parts/useHomeData';
import { arrivalFace, lockReasonKey, roomsFace, weatherFace } from './logic/tileFaces';
import { dateOnly } from './logic/accessWindow';

/**
 * The guest home: a launcher, not a document (docs/GUEST-REDESIGN.md).
 *
 * Top strip: greeting, dates, tier, the sync badge and the language switch.
 * Then the island from the water, the one memorable moment. Then six tiles in
 * a fixed order, each with one live line read from what is already on the
 * phone, and a quiet Sign out. No paragraphs.
 *
 * On the first open of a visit the island lifts, the greeting rises and the
 * tiles arrive 70 ms apart. A tap anywhere skips it, and under reduced motion
 * it does not play at all. Help lives in the round button, not in a tile.
 */
export function Home() {
  const { t, lang } = useT();
  const { session, signOut } = useAuth();
  const status = useSyncStatus(sync);
  const access = useAccess();
  const intro = useIntro();
  const [playing, setPlaying] = useState(intro);

  const stayId = session?.stay.id;
  const personalOpen = access?.personalOpen ?? false;
  const stay = useStayRow(stayId, status.online);
  const weather = useSavedWeather(status.online);
  const roomData = useRoomsAtAGlance(stayId, personalOpen, status.online);
  const sunshine = useSunshine(status.online);

  if (!session || !access) return null;

  const date = (day: string, style: 'long' | 'medium' = 'long') =>
    new Intl.DateTimeFormat(lang, { dateStyle: style, timeZone: 'UTC' }).format(new Date(`${dateOnly(day)}T12:00:00Z`));

  const tierLabel = t(
    session.stay.tier === 'all_inclusive' ? 'guest.stay.tier.all_inclusive' : 'guest.stay.tier.standard',
  );

  const arrivalLine =
    access.phase === 'planning' || access.phase === 'upcoming'
      ? access.daysToArrival === 0
        ? t('guest.home.arrives_today')
        : access.daysToArrival === 1
          ? t('guest.home.arrives_tomorrow')
          : t('guest.home.arrives_in', { count: access.daysToArrival })
      : null;

  const lockHint = t(lockReasonKey(access.phase, session.stay.status === 'cancelled'));

  // Arrival Guide
  const arrival = arrivalFace(access.phase, stay?.arrival_boat_time, access.daysToArrival);
  const arrivalLive =
    arrival.kind === 'boat'
      ? t('guest.tile.arrival_boat', { time: arrival.time })
      : arrival.kind === 'thanks'
        ? t('guest.tile.arrival_thanks')
        : arrival.kind === 'open_once'
          ? t('guest.tile.arrival_open_once')
          : null;
  const arrivalHint =
    arrival.kind === 'thanks'
      ? t('guest.tile.arrival_left', { date: date(session.stay.departs_on, 'medium') })
      : t('guest.tile.arrival_hint');

  // Weather
  const sky = weatherFace(weather.data, weather.now);
  let weatherLive: string | null = null;
  let weatherHint = t('guest.tile.weather_hint');
  if (sky.kind === 'reading') {
    const temp =
      sky.tempC === null
        ? '-'
        : t('guest.weather.temp', {
            temp: Math.round(sky.tempC),
            tempF: Math.round((sky.tempC * 9) / 5 + 32),
          });
    weatherLive = t('guest.tile.weather_live', {
      temp,
      sky: t(`guest.weather.kind.${sky.sky}`).toLocaleLowerCase(lang),
    });
    if (sky.showAge && sky.age.unit !== 'now') {
      weatherHint = t(`guest.weather.updated_${sky.age.unit}`, { count: sky.age.value });
    }
  } else {
    weatherHint = t('guest.tile.weather_none');
  }

  // Who sleeps where
  const rooms = roomsFace({ rooms: roomData.rooms, claims: roomData.claims, online: status.online });
  const roomsLive =
    rooms.kind === 'count'
      ? t('guest.tile.rooms_count', { used: rooms.used, total: rooms.total })
      : rooms.kind === 'none_picked'
        ? t('guest.tile.rooms_none')
        : null;
  const roomsHint = rooms.offline ? t('guest.tile.rooms_offline') : t('guest.tile.rooms_hint');

  const iconClass = 'h-[1.6rem] w-[1.6rem]';
  const tileDelay = (i: number) => delay(450 + i * 70);

  return (
    // Any tap during the opening moment ends it and shows the finished page.
    <div className={playing ? 'dia-intro' : undefined} onPointerDown={playing ? () => setPlaying(false) : undefined}>
      <MotionStyles />
      <Screen
        actions={
          <>
            <SyncBadge pending={status.pending} dead={status.dead} online={status.online} syncing={status.syncing} />
            <LangToggle />
          </>
        }
      >
        <h1 className="dia-rise font-display text-[2em] leading-[1.1] text-ink" style={delay(150)}>
          {t('guest.home.welcome', { name: session.guest.first_name })}
        </h1>
        <p className="dia-rise mt-1.5 text-inkMuted" style={delay(250)}>
          {t('guest.stay.dates_range', {
            from: date(session.stay.arrives_on),
            to: date(session.stay.departs_on),
          })}
        </p>
        <div className="dia-rise mt-3 flex flex-wrap gap-2" style={delay(300)}>
          <StatusPill tone="info">{tierLabel}</StatusPill>
          {arrivalLine && <StatusPill tone="neutral">{arrivalLine}</StatusPill>}
        </div>

        {/*
          A strip, not a hero: this screen's job is to get a thumb to a tile
          with no scroll first, and a tall decorative photo was pushing all
          six below the fold (reported 2026-09-26, "half the page is
          bullshit... gives you really no data on what it is").
        */}
        <div className="relative -mx-4 mt-4 h-16 overflow-hidden">
          <PhotoPanel src="/photos/island-from-water.webp" alt="" todo="the island from the water" />
        </div>

        {/*
          Every tile the same square shape (no `wide`), so the grid packs two
          to a row edge to edge: the whole set reads at a glance, the way a
          phone home screen does, instead of two rows eaten by wide tiles.
          max-w caps this at two columns even on a wide desktop browser (the
          shared reading width, 38rem, is wide enough to fit three 9.5rem
          tiles, which is what a guest on a laptop was seeing: a 3 plus 1 row
          with Translator sitting alone). auto-fit still collapses to one
          column at 200 percent zoom or on a narrow phone, unchanged.
        */}
        <nav aria-label={t('guest.home.tiles_label')} className="mt-4 max-w-[26rem]">
          <ul className="grid list-none grid-cols-[repeat(auto-fit,minmax(9.5rem,1fr))] gap-3 p-0">
            <Tile
              to="/llegada"
              icon={<BoatIcon className={iconClass} />}
              name={t('guest.tile.arrival')}
              live={arrivalLive}
              hint={arrivalHint}
              className="dia-rise"
              style={tileDelay(0)}
            />
            <Tile
              to="/casas"
              icon={<HouseIcon className={iconClass} />}
              name={t('guest.tile.house')}
              hint={t('guest.tile.house_hint')}
              className="dia-rise"
              style={tileDelay(1)}
            />
            <Tile
              to="/isla"
              icon={<PalmIcon className={iconClass} />}
              name={t('guest.tile.island')}
              hint={t('guest.tile.island_hint')}
              className="dia-rise"
              style={tileDelay(2)}
            />
            <Tile
              to="/clima"
              icon={<CloudSunIcon className={iconClass} />}
              name={t('guest.tile.weather')}
              live={weatherLive}
              hint={weatherHint}
              className="dia-rise"
              style={tileDelay(3)}
            />
            <Tile
              to="/traductor"
              icon={<BubblesIcon className={iconClass} />}
              name={t('guest.tile.translator')}
              hint={t('guest.tile.translator_hint')}
              locked={!personalOpen}
              lockedHint={lockHint}
              className="dia-rise"
              style={tileDelay(4)}
            />
            <Tile
              to="/cuartos"
              icon={<BedIcon className={iconClass} />}
              name={t('guest.tile.rooms')}
              live={roomsLive}
              hint={roomsHint}
              locked={!personalOpen}
              lockedHint={lockHint}
              className="dia-rise"
              style={tileDelay(5)}
            />
          </ul>
        </nav>

        {/*
          One quiet note under the launcher, never among the tappable tiles. It
          exists only while the count is real: whole days, and fresh data. A
          guest who sees nothing here has simply not been told something we
          cannot vouch for (docs/telemetry-contract.md, staleness).
        */}
        {sunshine && (
          <ul className="mt-3 grid max-w-[26rem] list-none grid-cols-1 p-0">
            <Tile
              icon={<SunIcon className={iconClass} />}
              name={t('guest.sunshine.name')}
              live={
                sunshine.days === 1
                  ? t('guest.sunshine.days_one')
                  : t('guest.sunshine.days', { count: sunshine.days })
              }
              hint={t('guest.sunshine.hint')}
              wide
              className="dia-rise"
              style={tileDelay(6)}
            />
          </ul>
        )}

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={signOut}
            className="min-h-tap px-4 text-caption text-inkFaint underline underline-offset-4 hover:text-ink"
          >
            {t('action.sign_out')}
          </button>
        </div>
      </Screen>
    </div>
  );
}
